import { useParams, Link } from 'react-router-dom';
import { useMemo, useState, useEffect } from 'react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/shared/SEOHead';
import { DocsNav } from '@/components/docs/DocsNav';
import { DocsSidebarNav, SidebarSection } from '@/components/docs/DocsSidebarNav';
import { MarkdownRenderer } from '@/components/shared/MarkdownRenderer';
import { useDataTypeDoc, useDataTypeDocs } from '@/hooks/useDataTypeDocs';
import { useAnalytics } from '@/hooks/useAnalytics';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { Hash, ChevronLeft, ChevronRight, Copy, Check } from 'lucide-react';
import * as Icons from 'lucide-react';

const categoryOrder = ['format', 'type', 'structure', 'other'];

const categoryLabels: Record<string, string> = {
  format: 'Formats',
  type: 'Types',
  structure: 'Structures',
  other: 'Other',
};

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Calendar: Icons.Calendar,
  Clock: Icons.Clock,
  Key: Icons.Key,
  Phone: Icons.Phone,
  DollarSign: Icons.DollarSign,
  MapPin: Icons.MapPin,
  ToggleRight: Icons.ToggleRight,
  FileText: Icons.FileText,
  Hash: Icons.Hash,
  Mail: Icons.Mail,
  Link: Icons.Link,
  Globe: Icons.Globe,
  User: Icons.User,
};

export default function DataTypeArticle() {
  const { slug } = useParams<{ slug: string }>();
  const { data: dataType, isLoading, error } = useDataTypeDoc(slug || '');
  const { data: allTypes } = useDataTypeDocs();
  const [copied, setCopied] = useState(false);
  const { trackDocView } = useAnalytics();

  // Track data type view
  useEffect(() => {
    if (dataType && slug) {
      trackDocView('datatype', slug);
    }
  }, [dataType, slug, trackDocView]);

  // Group docs by category for sidebar
  const groupedDocs = useMemo(() => {
    if (!allTypes) return {} as Record<string, typeof allTypes>;
    return allTypes.reduce((acc, doc) => {
      const category = doc.category || 'other';
      if (!acc[category]) acc[category] = [];
      acc[category].push(doc);
      return acc;
    }, {} as Record<string, typeof allTypes>);
  }, [allTypes]);

  // Build sidebar sections
  const sidebarSections: SidebarSection[] = useMemo(() => {
    if (!allTypes) return [];
    
    return categoryOrder
      .filter((cat) => groupedDocs[cat]?.length > 0)
      .map((category) => ({
        title: categoryLabels[category] || category,
        slug: category,
        items: groupedDocs[category]?.map((doc) => ({
          title: doc.name,
          slug: doc.slug,
          href: `/docs/data-types/${doc.slug}`,
        })) || [],
      }));
  }, [allTypes, groupedDocs]);

  // Find prev/next types
  const { prevType, nextType } = useMemo(() => {
    if (!allTypes || !slug) return { prevType: null, nextType: null };
    
    const currentIndex = allTypes.findIndex((t) => t.slug === slug);
    
    return {
      prevType: currentIndex > 0 ? allTypes[currentIndex - 1] : null,
      nextType: currentIndex < allTypes.length - 1 ? allTypes[currentIndex + 1] : null,
    };
  }, [allTypes, slug]);

  const getIconComponent = (iconName: string | null | undefined) => {
    if (!iconName) return Icons.FileText;
    return iconMap[iconName] || Icons.FileText;
  };

  const handleCopyRegex = async () => {
    if (dataType?.regex_pattern) {
      await navigator.clipboard.writeText(dataType.regex_pattern);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const IconComponent = dataType ? getIconComponent(dataType.icon) : Icons.Hash;

  return (
    <Layout>
      <SEOHead 
        path={`/docs/data-types/${slug}`}
        title={dataType ? `${dataType.name} | Data Types | AutomotiveAPI Docs` : undefined}
        description={dataType?.short_description || undefined}
      />
      
      <DocsNav />
      
      <div className="container py-8">
        <div className="flex gap-8">
          {/* Sidebar */}
          <DocsSidebarNav sections={sidebarSections} className="hidden lg:block" />
          
          {/* Main Content */}
          <main className="flex-1 min-w-0 max-w-3xl">
            {/* Breadcrumb */}
            <Breadcrumb className="mb-6">
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/docs">Docs</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/docs/data-types">Data Types</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                {dataType && (
                  <>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                      <BreadcrumbPage>{dataType.name}</BreadcrumbPage>
                    </BreadcrumbItem>
                  </>
                )}
              </BreadcrumbList>
            </Breadcrumb>
            
            {/* Loading State */}
            {isLoading && (
              <div className="space-y-4">
                <Skeleton className="h-10 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
                <div className="mt-8 space-y-3">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                </div>
              </div>
            )}
            
            {/* Error State */}
            {error && (
              <div className="text-center py-12">
                <Hash className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h2 className="text-xl font-semibold mb-2">Data type not found</h2>
                <p className="text-muted-foreground mb-4">
                  The data type you're looking for doesn't exist or has been moved.
                </p>
                <Button asChild variant="outline">
                  <Link to="/docs/data-types">Browse all data types</Link>
                </Button>
              </div>
            )}
            
            {/* Article Content */}
            {dataType && (
              <>
                <header className="mb-8">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="p-3 rounded-lg bg-primary/10">
                      <IconComponent className="h-6 w-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h1 className="text-3xl font-bold">{dataType.name}</h1>
                        <Badge variant="outline" className="capitalize">
                          {dataType.category}
                        </Badge>
                      </div>
                      {dataType.short_description && (
                        <p className="text-lg text-muted-foreground">
                          {dataType.short_description}
                        </p>
                      )}
                    </div>
                  </div>
                </header>
                
                {/* Example and Regex Pattern */}
                <div className="flex flex-wrap gap-6 mb-8 p-4 bg-muted/30 rounded-lg border">
                  {dataType.example_value && (
                    <div className="space-y-1">
                      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Example</span>
                      <code className="block text-sm font-mono bg-background px-3 py-2 rounded-md border">
                        {dataType.example_value}
                      </code>
                    </div>
                  )}
                  {dataType.regex_pattern && (
                    <div className="space-y-1 flex-1 min-w-[200px]">
                      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Regex Pattern</span>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 text-xs font-mono bg-background px-3 py-2 rounded-md border overflow-x-auto">
                          {dataType.regex_pattern}
                        </code>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 shrink-0"
                          onClick={handleCopyRegex}
                        >
                          {copied ? (
                            <Check className="h-4 w-4 text-green-500" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Full markdown content */}
                {dataType.content && (
                  <article className="prose prose-slate dark:prose-invert max-w-none">
                    <MarkdownRenderer 
                      content={dataType.content} 
                      generateHeadingIds 
                    />
                  </article>
                )}
                
                {/* Prev/Next Navigation */}
                <nav className="mt-12 pt-8 border-t flex items-center justify-between">
                  {prevType ? (
                    <Link 
                      to={`/docs/data-types/${prevType.slug}`}
                      className="flex items-center gap-2 text-sm hover:text-primary transition-colors"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      <div className="text-right">
                        <div className="text-muted-foreground text-xs">Previous</div>
                        <div className="font-medium">{prevType.name}</div>
                      </div>
                    </Link>
                  ) : (
                    <div />
                  )}
                  
                  {nextType && (
                    <Link 
                      to={`/docs/data-types/${nextType.slug}`}
                      className="flex items-center gap-2 text-sm hover:text-primary transition-colors"
                    >
                      <div className="text-left">
                        <div className="text-muted-foreground text-xs">Next</div>
                        <div className="font-medium">{nextType.name}</div>
                      </div>
                      <ChevronRight className="h-4 w-4" />
                    </Link>
                  )}
                </nav>
              </>
            )}
          </main>
        </div>
      </div>
    </Layout>
  );
}
